"use client"

/**
 * /actions — sections 03 "Strategies" + 04 "Prepared work".
 *
 * Ported from Actions.dc.html (STRAT data, paintStatic cards, paintStrat,
 * paintPrev, art(), kindIcon(), prevBody()). The two sections share one
 * selection: picking a strategy card (03) swaps the handover package (04);
 * picking a prepared-work item swaps the preview. Auto-advances until the
 * visitor interacts. Reduced-motion disables the cycles and entrances.
 */

import { useEffect, useState } from "react"

const MONO = "var(--vx-mono)"
const SERIF = "var(--vx-serif)"

type WorkItem = [title: string, desc: string, kind: string]
interface Strategy {
  n: string
  c: string
  e: string
  why: string
  work: WorkItem[]
}

const STRAT: Strategy[] = [
  {
    n: "Create the comparison buyers are missing",
    c: "Content",
    e: "Medium effort",
    why: "No page of yours compares travel headphones, so AI has nothing of yours to cite.",
    work: [
      ["Comparison-page structure", "Sections, headings and table layout", "brief"],
      ["Comparison table", "Battery, weight, ANC and price across five models", "table"],
      ["Direct-answer copy", "Short answers for the 12 affected questions", "copy"],
      ["Titles, H1 and metadata", "Written for the answer, not just the SERP", "meta"],
      ["Structured data", "Product and FAQ markup to add", "schema"],
      ["Internal-link plan", "Six existing pages to link from", "links"],
    ],
  },
  {
    n: "Prove the noise-cancelling claims",
    c: "Proof",
    e: "Low effort",
    why: "Your claims are repeated without evidence, so reviewers and AI both discount them.",
    work: [
      ["Claims and proof checklist", "Every claim, with the evidence it needs", "check"],
      ["Test-data reference sheet", "Measured figures to publish", "table"],
      ["FAQ content", "Nine questions about accuracy and comfort", "copy"],
      ["Developer instructions", "Where the proof block goes on the page", "dev"],
    ],
  },
  {
    n: "Fix the technical basics on your pages",
    c: "Technical",
    e: "Low effort",
    why: "Two pages carry a duplicate title, one has no H1, and product markup is missing.",
    work: [
      ["H1 and heading fixes", "“Sennheiser travel headphones” as H1 on two pages", "meta"],
      ["Titles and meta descriptions", "Rewrite three duplicate titles", "meta"],
      ["Product and FAQ schema", "JSON-LD markup for two product pages", "schema"],
      ["Internal links", "Six broken links, six new links to add", "links"],
      ["Page structure", "Move the battery answer above the fold", "brief"],
      ["Developer instructions", "Exact changes, page by page", "dev"],
    ],
  },
  {
    n: "Get cited by the sources AI reads",
    c: "Sources",
    e: "Higher effort",
    why: "Three frequently cited domains never mention you, including one measured-test site.",
    work: [
      ["Source-opportunity brief", "Three domains, with the angle for each", "sources"],
      ["Outreach drafts", "Two review sites and one roundup", "mail"],
      ["Listing-completion checklist", "Four incomplete profiles", "check"],
      ["Factual-correction requests", "Two directories with outdated specs", "mail"],
      ["Product-information reference sheet", "One source of truth for reviewers", "table"],
    ],
  },
]

function fav(d: string) {
  return `https://www.google.com/s2/favicons?sz=64&domain=${d}`
}

/* ── Card mini-illustrations (art i) ─────────────────────────────────────── */
function Line({ w, o, d }: { w: string; o: string; d: number }) {
  return (
    <span
      style={{
        display: "block",
        height: 4,
        borderRadius: 2,
        width: w,
        background: `rgba(207,238,247,${o})`,
        animation: `vxaDrift ${4 + d}s ${d}s ease-in-out infinite`,
      }}
    />
  )
}

function CardArt({ i }: { i: number }) {
  if (i === 0) {
    return (
      <span style={{ display: "flex", alignItems: "flex-end", gap: 6, height: 54 }}>
        <span style={{ display: "flex", flexDirection: "column", gap: 4, flex: 1, padding: 8, borderRadius: 8, border: "1px solid rgba(107,213,236,.3)", background: "rgba(107,213,236,.06)" }}>
          <Line w="70%" o=".85" d={0} />
          <Line w="100%" o=".3" d={0.4} />
          <Line w="88%" o=".3" d={0.8} />
        </span>
        <span style={{ display: "flex", flexDirection: "column", gap: 4, flex: 1, padding: 8, borderRadius: 8, border: "1px solid rgba(160,196,224,.18)" }}>
          <Line w="60%" o=".55" d={0.6} />
          <Line w="100%" o=".22" d={1} />
          <Line w="80%" o=".22" d={1.4} />
        </span>
        <span style={{ display: "flex", flexDirection: "column", gap: 4, flex: 1, padding: 8, borderRadius: 8, border: "1px dashed rgba(228,203,162,.45)" }}>
          <Line w="55%" o=".35" d={0.3} />
          <Line w="90%" o=".16" d={0.9} />
        </span>
      </span>
    )
  }
  if (i === 1) {
    return (
      <span style={{ display: "flex", alignItems: "center", gap: 10, height: 54 }}>
        <span style={{ position: "relative", display: "flex", alignItems: "center", justifyContent: "center", width: 40, height: 44, borderRadius: "6px 6px 14px 14px", border: "1.5px solid rgba(143,217,196,.65)", animation: "vxaRing 3.6s ease-in-out infinite" }}>
          <span style={{ width: 13, height: 6, borderLeft: "1.6px solid #8FD9C4", borderBottom: "1.6px solid #8FD9C4", transform: "rotate(-45deg) translate(1px, -1px)" }} />
        </span>
        <span style={{ display: "flex", flexDirection: "column", gap: 5, flex: 1 }}>
          <Line w="82%" o=".5" d={0.2} />
          <Line w="62%" o=".3" d={0.7} />
          <Line w="72%" o=".3" d={1.1} />
        </span>
      </span>
    )
  }
  if (i === 2) {
    return (
      <span style={{ display: "flex", alignItems: "center", gap: 8, height: 54, padding: "0 2px" }}>
        <span style={{ font: `500 20px/1 ${MONO}`, color: "rgba(107,213,236,.7)", animation: "vxaDrift 5s ease-in-out infinite" }}>&lt;</span>
        <span style={{ display: "flex", flexDirection: "column", gap: 5, flex: 1 }}>
          <span style={{ font: `500 9px/1 ${MONO}`, letterSpacing: ".1em", color: "rgba(207,238,247,.75)" }}>H1</span>
          <Line w="92%" o=".28" d={0.5} />
          <Line w="66%" o=".28" d={0.9} />
        </span>
        <span style={{ font: `500 20px/1 ${MONO}`, color: "rgba(107,213,236,.7)", animation: "vxaDrift 5s .8s ease-in-out infinite" }}>/&gt;</span>
      </span>
    )
  }
  return (
    <span style={{ display: "flex", alignItems: "center", gap: 7, height: 54 }}>
      {["rtings.com", "theverge.com", "reddit.com"].map((d, k) => (
        <span key={d} style={{ display: "flex", alignItems: "center", justifyContent: "center", width: 34, height: 34, borderRadius: 9, border: "1px solid rgba(160,196,224,.2)", background: "#04070C", animation: `vxaDrift ${4.4 + k * 0.4}s ${k * 0.5}s ease-in-out infinite` }}>
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img src={fav(d)} alt="" width={17} height={17} style={{ display: "block", borderRadius: 4 }} />
        </span>
      ))}
      <span aria-hidden="true" style={{ width: 14, height: 1, background: "rgba(228,203,162,.4)" }} />
      <span style={{ display: "flex", alignItems: "center", justifyContent: "center", width: 34, height: 34, borderRadius: 9, border: "1px dashed rgba(228,203,162,.5)" }}>
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img src={fav("sennheiser.com")} alt="" width={17} height={17} style={{ display: "block", borderRadius: 4, filter: "grayscale(1)", opacity: 0.7 }} />
      </span>
    </span>
  )
}

/* ── Prepared-work list-item icons ───────────────────────────────────────── */
function KindIcon({ kind, on }: { kind: string; on: boolean }) {
  const c = on ? "#8FD9C4" : "rgba(190,233,245,.7)"
  const boxStyle: React.CSSProperties = {
    flex: "none",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: 28,
    height: 28,
    borderRadius: 8,
    border: `1px solid ${on ? "rgba(143,217,196,.4)" : "rgba(160,196,224,.2)"}`,
    background: on ? "rgba(143,217,196,.09)" : "rgba(120,170,210,.04)",
  }
  let inner: React.ReactNode
  if (kind === "table") {
    inner = (
      <span style={{ display: "grid", gridTemplateColumns: "repeat(2, 6px)", gridTemplateRows: "repeat(2, 5px)", gap: 2 }}>
        <span style={{ background: c, opacity: 0.9 }} />
        <span style={{ background: c, opacity: 0.45 }} />
        <span style={{ background: c, opacity: 0.45 }} />
        <span style={{ background: c, opacity: 0.45 }} />
      </span>
    )
  } else if (kind === "schema") {
    inner = <span style={{ font: `500 11px/1 ${MONO}`, color: c }}>{"{ }"}</span>
  } else if (kind === "copy") {
    inner = (
      <span style={{ display: "flex", flexDirection: "column", gap: 2.5, width: 14 }}>
        <span style={{ height: 1.6, background: c, opacity: 0.9 }} />
        <span style={{ height: 1.6, background: c, opacity: 0.5 }} />
        <span style={{ height: 1.6, width: 9, background: c, opacity: 0.5 }} />
      </span>
    )
  } else if (kind === "meta") {
    inner = <span style={{ font: `600 9px/1 ${MONO}`, letterSpacing: ".04em", color: c }}>H1</span>
  } else if (kind === "links") {
    inner = (
      <span style={{ display: "flex", alignItems: "center" }}>
        <span style={{ width: 8, height: 8, border: `1.4px solid ${c}`, borderRadius: "50%" }} />
        <span style={{ width: 5, height: 1.4, background: c }} />
        <span style={{ width: 8, height: 8, border: `1.4px solid ${c}`, borderRadius: "50%", opacity: 0.6 }} />
      </span>
    )
  } else if (kind === "check") {
    inner = (
      <span style={{ display: "flex", flexDirection: "column", gap: 3 }}>
        <span style={{ display: "flex", alignItems: "center", gap: 3 }}>
          <span style={{ width: 5, height: 5, border: `1.2px solid ${c}`, borderRadius: 1 }} />
          <span style={{ width: 8, height: 1.4, background: c, opacity: 0.6 }} />
        </span>
        <span style={{ display: "flex", alignItems: "center", gap: 3 }}>
          <span style={{ width: 5, height: 5, background: c, borderRadius: 1 }} />
          <span style={{ width: 8, height: 1.4, background: c, opacity: 0.6 }} />
        </span>
      </span>
    )
  } else if (kind === "dev") {
    inner = <span style={{ font: `500 10px/1 ${MONO}`, color: c }}>&lt;/&gt;</span>
  } else if (kind === "sources") {
    inner = (
      <span style={{ width: 13, height: 13, borderRadius: "50%", border: `1.3px solid ${c}`, position: "relative" }}>
        <span style={{ position: "absolute", left: -1, right: -1, top: 5, height: 1.3, background: c }} />
        <span style={{ position: "absolute", top: -1, bottom: -1, left: 5, width: 1.3, background: c, opacity: 0.7 }} />
      </span>
    )
  } else if (kind === "mail") {
    inner = (
      <span style={{ width: 15, height: 11, border: `1.3px solid ${c}`, borderRadius: 2, position: "relative", overflow: "hidden" }}>
        <span style={{ position: "absolute", left: 1, top: 1, width: 7, height: 7, borderRight: `1.3px solid ${c}`, borderBottom: `1.3px solid ${c}`, transform: "rotate(-45deg)" }} />
      </span>
    )
  } else {
    inner = (
      <span style={{ display: "flex", flexDirection: "column", gap: 2.5, width: 13 }}>
        <span style={{ height: 1.6, width: 7, background: c, opacity: 0.9 }} />
        <span style={{ height: 1.6, background: c, opacity: 0.45 }} />
        <span style={{ height: 1.6, background: c, opacity: 0.45 }} />
        <span style={{ height: 1.6, width: 9, background: c, opacity: 0.45 }} />
      </span>
    )
  }
  return (
    <span aria-hidden="true" style={boxStyle}>
      {inner}
    </span>
  )
}

/* ── Preview bodies, one per deliverable kind ────────────────────────────── */
function Surf({ children, pad }: { children: React.ReactNode; pad?: string | number }) {
  return (
    <div style={{ borderRadius: 13, border: "1px solid rgba(160,196,224,.16)", background: "#04070C", overflow: "hidden", padding: pad ?? 0 }}>
      {children}
    </div>
  )
}

function PrevBody({ kind }: { kind: string }) {
  if (kind === "table") {
    const rows: [string, string, string, string, string][] = [
      ["sony.com", "Sony WH-1000XM5", "30 h", "250 g", "4.4"],
      ["bose.com", "Bose QC Ultra", "24 h", "254 g", "4.5"],
      ["sennheiser.com", "Sennheiser Momentum 4", "60 h", "293 g", "4.3"],
      ["apple.com", "AirPods Max", "20 h", "386 g", "4.2"],
    ]
    return (
      <Surf>
        <div style={{ display: "grid", gridTemplateColumns: "1.6fr .7fr .7fr .7fr", gap: 0 }}>
          {["Model", "Battery", "Weight", "ANC score"].map((h) => (
            <div key={h} style={{ padding: "11px 13px", borderBottom: "1px solid rgba(160,196,224,.14)", font: `500 9px/1 ${MONO}`, letterSpacing: ".12em", textTransform: "uppercase", color: "rgba(190,233,245,.8)" }}>
              {h}
            </div>
          ))}
          {rows.map(([d, n, b, w, a], i) => {
            const own = d === "sennheiser.com"
            const cell = (v: string, first?: boolean) => (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 9,
                  padding: "12px 13px",
                  borderBottom: i < rows.length - 1 ? "1px solid rgba(160,196,224,.08)" : undefined,
                  background: own ? "rgba(143,217,196,.05)" : "transparent",
                  fontSize: 13,
                  color: own ? "#DFF3EA" : "rgba(240,245,249,.86)",
                }}
              >
                {first ? (
                  // eslint-disable-next-line @next/next/no-img-element
                  <img src={fav(d)} alt="" width={16} height={16} style={{ flex: "none", borderRadius: 4 }} />
                ) : null}
                <span style={{ minWidth: 0, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{v}</span>
              </div>
            )
            return (
              <div key={d} style={{ display: "contents" }}>
                {cell(n, true)}
                {cell(b)}
                {cell(w)}
                {cell(a)}
              </div>
            )
          })}
        </div>
      </Surf>
    )
  }

  if (kind === "schema") {
    const L: [React.ReactNode, string][] = [
      ["{", "rgba(232,238,244,.5)"],
      [<>{"  \"@type\": "}<span style={{ color: "#8FD9C4" }}>&quot;Product&quot;</span>,</>, "rgba(190,233,245,.85)"],
      [<>{"  \"name\": "}<span style={{ color: "#8FD9C4" }}>&quot;Sennheiser Momentum 4&quot;</span>,</>, "rgba(190,233,245,.85)"],
      [<>{"  \"aggregateRating\": { \"ratingValue\": "}<span style={{ color: "#E4CBA2" }}>4.3</span>{" },"}</>, "rgba(190,233,245,.85)"],
      [<>{"  \"additionalProperty\": "}<span style={{ color: "#8FD9C4" }}>&quot;60 h battery&quot;</span></>, "rgba(190,233,245,.85)"],
      ["}", "rgba(232,238,244,.5)"],
    ]
    return (
      <Surf>
        <div style={{ padding: "14px 16px", overflowX: "auto" }}>
          {L.map(([t, c], i) => (
            <div key={i} style={{ font: `400 12px/1.8 ${MONO}`, color: c, whiteSpace: "pre" }}>
              {t}
            </div>
          ))}
        </div>
      </Surf>
    )
  }

  if (kind === "copy") {
    return (
      <Surf>
        <div style={{ padding: 16 }}>
          <div style={{ font: `500 9px/1 ${MONO}`, letterSpacing: ".12em", textTransform: "uppercase", color: "rgba(190,233,245,.8)" }}>
            Answer for “best battery life on long flights”
          </div>
          <div style={{ fontSize: 14, lineHeight: 1.7, color: "rgba(240,245,249,.9)", marginTop: 11, paddingLeft: 13, borderLeft: "2px solid rgba(143,217,196,.5)" }}>
            The Momentum 4 runs about 60 hours with noise cancelling on, roughly twice the Sony WH-1000XM5 and enough for a return long-haul trip without charging.
          </div>
          <div style={{ font: `400 10.5px/1.6 ${MONO}`, letterSpacing: ".06em", color: "rgba(232,238,244,.5)", marginTop: 12 }}>
            Answers 4 of the 12 affected questions · 44 words
          </div>
        </div>
      </Surf>
    )
  }

  if (kind === "meta") {
    const rows: [string, string, string, string][] = [
      ["Current H1", "Momentum 4", "rgba(232,238,244,.45)", "line-through"],
      ["Prepared H1", "Sennheiser Momentum 4 travel headphones", "#DFF3EA", "none"],
      ["Current title", "Momentum 4 | Sennheiser", "rgba(232,238,244,.45)", "line-through"],
      ["Prepared title", "Momentum 4: 60-hour battery for long flights", "#DFF3EA", "none"],
    ]
    return (
      <Surf>
        <div style={{ padding: 16 }}>
          {rows.map(([l, v, c, d]) => (
            <div key={l} style={{ display: "flex", alignItems: "baseline", gap: 12, padding: "9px 0", borderBottom: "1px solid rgba(160,196,224,.08)" }}>
              <span style={{ flex: "none", width: 92, font: `400 9.5px/1.4 ${MONO}`, letterSpacing: ".08em", textTransform: "uppercase", color: "rgba(232,238,244,.45)" }}>
                {l}
              </span>
              <span style={{ fontSize: 13.5, color: c, textDecoration: d, textDecorationColor: "rgba(228,203,162,.7)" }}>{v}</span>
            </div>
          ))}
        </div>
      </Surf>
    )
  }

  if (kind === "links") {
    const rows: [string, string, string][] = [
      ["Travel guide → Momentum 4", "Add", "#8FD9C4"],
      ["Homepage → comparison page", "Add", "#8FD9C4"],
      ["Old review → 404", "Broken", "#E4CBA2"],
      ["Category → discontinued page", "Broken", "#E4CBA2"],
    ]
    return (
      <Surf>
        <div style={{ padding: "14px 16px" }}>
          {rows.map(([t, s, c]) => (
            <div key={t} style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 0", borderBottom: "1px solid rgba(160,196,224,.08)" }}>
              <span style={{ flex: 1, font: `400 12.5px/1.4 ${MONO}`, color: "rgba(240,245,249,.86)" }}>{t}</span>
              <span style={{ font: `500 8.5px/1 ${MONO}`, letterSpacing: ".12em", textTransform: "uppercase", color: c }}>{s}</span>
            </div>
          ))}
        </div>
      </Surf>
    )
  }

  if (kind === "check") {
    const rows: [string, number][] = [
      ["“Best-in-class ANC”: needs measured figure", 1],
      ["“60-hour battery”: test data attached", 0],
      ["“Lightest in class”: remove, not supportable", 1],
      ["Comfort claim: cite the 10-hour wear test", 1],
    ]
    return (
      <Surf>
        <div style={{ padding: "14px 16px" }}>
          {rows.map(([t, open]) => (
            <div key={t} style={{ display: "flex", alignItems: "center", gap: 11, padding: "10px 0", borderBottom: "1px solid rgba(160,196,224,.08)" }}>
              <span
                aria-hidden="true"
                style={{
                  flex: "none",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: 16,
                  height: 16,
                  borderRadius: 4,
                  border: `1.2px solid ${open ? "rgba(228,203,162,.6)" : "rgba(143,217,196,.7)"}`,
                  background: open ? "transparent" : "rgba(143,217,196,.15)",
                }}
              >
                {open ? null : (
                  <span style={{ width: 7, height: 3.5, borderLeft: "1.3px solid #8FD9C4", borderBottom: "1.3px solid #8FD9C4", transform: "rotate(-45deg) translate(.5px, -.5px)" }} />
                )}
              </span>
              <span style={{ fontSize: 13, color: "rgba(240,245,249,.86)" }}>{t}</span>
            </div>
          ))}
        </div>
      </Surf>
    )
  }

  if (kind === "dev") {
    const rows: [string, string][] = [
      ["/momentum-4", "Add H1, move battery block above the fold"],
      ["/momentum-4", "Insert Product + FAQ JSON-LD in <head>"],
      ["/travel-headphones", "New page from the prepared structure"],
      ["/reviews/legacy", "Redirect 301 → /momentum-4"],
    ]
    return (
      <Surf>
        <div style={{ padding: "14px 16px" }}>
          {rows.map(([f, t], i) => (
            <div key={i} style={{ display: "flex", gap: 12, padding: "9px 0", borderBottom: "1px solid rgba(160,196,224,.08)" }}>
              <span style={{ flex: "none", width: 148, font: `400 11.5px/1.5 ${MONO}`, color: "rgba(190,233,245,.8)" }}>{f}</span>
              <span style={{ fontSize: 13, lineHeight: 1.5, color: "rgba(240,245,249,.84)" }}>{t}</span>
            </div>
          ))}
        </div>
      </Surf>
    )
  }

  if (kind === "sources") {
    const rows: [string, string, string][] = [
      ["rtings.com", "Measured-test site · cited 19×", "Submit battery + ANC test data"],
      ["theverge.com", "Travel roundup · cited 14×", "Pitch inclusion for the 60-hour angle"],
      ["reddit.com", "r/headphones flight threads · cited 9×", "Correct outdated spec claims"],
    ]
    return (
      <Surf>
        <div style={{ padding: "14px 16px" }}>
          {rows.map(([d, m, a]) => (
            <div key={d} style={{ display: "flex", alignItems: "center", gap: 12, padding: "11px 0", borderBottom: "1px solid rgba(160,196,224,.08)" }}>
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img src={fav(d)} alt="" width={20} height={20} style={{ flex: "none", borderRadius: 5 }} />
              <span style={{ flex: 1, minWidth: 0 }}>
                <span style={{ display: "block", fontSize: 13, color: "rgba(240,245,249,.9)" }}>{a}</span>
                <span style={{ display: "block", font: `400 10px/1.4 ${MONO}`, letterSpacing: ".05em", color: "rgba(232,238,244,.5)", marginTop: 3 }}>{m}</span>
              </span>
            </div>
          ))}
        </div>
      </Surf>
    )
  }

  if (kind === "mail") {
    return (
      <Surf>
        <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 16px", borderBottom: "1px solid rgba(160,196,224,.12)" }}>
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img src={fav("rtings.com")} alt="" width={18} height={18} style={{ borderRadius: 4 }} />
          <span style={{ font: `400 11.5px/1.4 ${MONO}`, color: "rgba(190,233,245,.8)" }}>To: editorial · rtings.com</span>
        </div>
        <div style={{ padding: "14px 16px" }}>
          <div style={{ fontSize: 13.5, fontWeight: 500, color: "#F2F7FA" }}>Battery and ANC test data for the Momentum 4</div>
          <div style={{ fontSize: 13, lineHeight: 1.7, color: "rgba(240,245,249,.82)", marginTop: 9 }}>
            Your travel headphone table lists the Momentum 4 without runtime data. We can share the measured 60-hour ANC-on figure and the test conditions behind it.
          </div>
          <div style={{ font: `400 10px/1.6 ${MONO}`, letterSpacing: ".06em", color: "rgba(232,238,244,.5)", marginTop: 12 }}>
            Draft only. Your team sends it, Velova never does.
          </div>
        </div>
      </Surf>
    )
  }

  // default: brief
  const rows: [string, string, string][] = [
    ["01", "Who this page is for", "Frequent long-haul flyers comparing on battery and comfort"],
    ["02", "What buyers compare", "Battery, comfort, call quality, price, in that order"],
    ["03", "Comparison table", "Five models, four criteria"],
    ["04", "Direct answer block", "Quotable answer for the 12 tracked questions"],
    ["05", "Proof and sources", "Measured figures with citations"],
  ]
  return (
    <Surf>
      <div style={{ padding: "14px 16px" }}>
        {rows.map(([n, t, d]) => (
          <div key={n} style={{ display: "flex", gap: 13, padding: "10px 0", borderBottom: "1px solid rgba(160,196,224,.08)" }}>
            <span style={{ flex: "none", font: `400 10px/1.6 ${MONO}`, color: "rgba(107,213,236,.7)" }}>{n}</span>
            <span style={{ minWidth: 0 }}>
              <span style={{ display: "block", fontSize: 13.5, color: "rgba(240,245,249,.9)" }}>{t}</span>
              <span style={{ display: "block", fontSize: 12.5, lineHeight: 1.5, color: "rgba(232,238,244,.58)", marginTop: 3 }}>{d}</span>
            </span>
          </div>
        ))}
      </div>
    </Surf>
  )
}

/* ── Section step scaffold (matches the server-rendered sections) ─────────── */
function StepShell({
  id,
  n,
  title,
  sub,
  children,
}: {
  id: string
  n: string
  title: string
  sub: string
  children: React.ReactNode
}) {
  return (
    <div id={id} className="vx-section" style={{ position: "relative", marginTop: 84, scrollMarginTop: 24 }}>
      <div className="vx-section-col" style={{ position: "relative" }}>
        <span style={{ font: `500 12px/1 ${MONO}`, letterSpacing: ".16em", textTransform: "uppercase", color: "rgba(107,213,236,.9)" }}>{n}</span>
        <span aria-hidden="true" style={{ position: "absolute", left: 5, top: 30, bottom: -30, width: 1, background: "linear-gradient(180deg, rgba(107,213,236,.45), rgba(107,213,236,.04))" }} />
      </div>
      <div style={{ minWidth: 0 }}>
        <h2 style={{ margin: "-6px 0 0", fontSize: "clamp(27px, 4.4vw, 38px)", lineHeight: 1.06, fontWeight: 500, letterSpacing: "-.033em", color: "#F4F8FB" }}>{title}</h2>
        <div style={{ fontFamily: SERIF, fontStyle: "italic", fontSize: "clamp(19px, 2.6vw, 23px)", color: "rgba(178,224,240,.9)", marginTop: 6 }}>{sub}</div>
        {children}
      </div>
    </div>
  )
}

export function StrategyWork() {
  const [strat, setStrat] = useState(0)
  const [item, setItem] = useState(0)
  const [paused, setPaused] = useState(false)
  const [reduced, setReduced] = useState(false)

  useEffect(() => {
    if (typeof window === "undefined") return
    setReduced(window.matchMedia("(prefers-reduced-motion: reduce)").matches)
  }, [])

  // Reset the previewed item whenever the strategy changes.
  useEffect(() => {
    setItem(0)
  }, [strat])

  // Auto-advance the strategy until the visitor interacts.
  useEffect(() => {
    if (paused || reduced) return
    const id = setInterval(() => setStrat((s) => (s + 1) % STRAT.length), 13000)
    return () => clearInterval(id)
  }, [paused, reduced])

  // Auto-advance the previewed item within the active strategy.
  useEffect(() => {
    if (paused || reduced) return
    const len = STRAT[strat].work.length
    const id = setInterval(() => setItem((it) => (it + 1) % len), 3200)
    return () => clearInterval(id)
  }, [paused, reduced, strat])

  const S = STRAT[strat]
  // `item` can briefly point past the new strategy's shorter `work` array on
  // the render right after `strat` changes (before the reset effect runs), so
  // clamp it to keep the preview in bounds.
  const safeItem = item < S.work.length ? item : 0
  const activeItem = S.work[safeItem]

  return (
    <>
      {/* 03 · Strategies */}
      <StepShell
        id="c03"
        n="03"
        title="Several routes to the same outcome."
        sub="You choose: one, or a combination."
      >
        {/* flexWrap + a shrinkable label: at 390px the caption's intrinsic
            width (389px) exceeded the 346px column, and `flex:none` stopped it
            shrinking, so it ran off the edge. It now wraps under the rule. */}
        <div style={{ display: "flex", alignItems: "center", gap: 14, marginTop: 18, flexWrap: "wrap" }}>
          <span aria-hidden="true" style={{ flex: "1 1 60px", height: 1, background: "linear-gradient(90deg, rgba(107,213,236,.28), transparent)" }} />
          <span style={{ flex: "1 1 auto", minWidth: 0, font: `400 10px/1.6 ${MONO}`, letterSpacing: ".06em", color: "rgba(232,238,244,.55)" }}>
            Three proposals included by default · more can be generated
          </span>
        </div>

        <div className="vx-strat-cards" style={{ marginTop: 16 }}>
          {STRAT.map((s, i) => {
            const on = i === strat
            return (
              <div
                key={s.c}
                data-s="card"
                role="button"
                tabIndex={0}
                aria-pressed={on}
                onClick={() => {
                  setPaused(true)
                  setStrat(i)
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault()
                    setPaused(true)
                    setStrat(i)
                  }
                }}
                style={{
                  boxSizing: "border-box",
                  display: "flex",
                  flexDirection: "column",
                  padding: 18,
                  borderRadius: 16,
                  border: `1px solid ${on ? "rgba(107,213,236,.5)" : "rgba(160,196,224,.16)"}`,
                  background: on ? "linear-gradient(180deg, rgba(107,213,236,.1), #04070C 62%)" : "linear-gradient(180deg, #05080D, #03050A)",
                  boxShadow: on ? "0 28px 70px -44px rgba(107,213,236,.6)" : "none",
                  cursor: "pointer",
                }}
              >
                <div style={{ height: 54, margin: "-2px 0 14px" }}>
                  <CardArt i={i} />
                </div>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
                  <span style={{ font: `500 9px/1 ${MONO}`, letterSpacing: ".13em", textTransform: "uppercase", color: on ? "#DEF6FC" : "rgba(232,238,244,.6)" }}>{s.c}</span>
                  <span
                    style={{
                      font: `500 8.5px/1 ${MONO}`,
                      letterSpacing: ".12em",
                      textTransform: "uppercase",
                      color: on ? "#6BD5EC" : "rgba(232,238,244,.55)",
                      border: on ? "1px solid rgba(107,213,236,.45)" : undefined,
                      padding: on ? "4px 7px" : undefined,
                      borderRadius: on ? 6 : undefined,
                    }}
                  >
                    {on ? "Selected" : "Select"}
                  </span>
                </div>
                <div style={{ fontSize: 16, lineHeight: 1.35, color: on ? "#fff" : "rgba(246,250,253,.92)", marginTop: 13 }}>{s.n}</div>
                <div style={{ fontSize: 13, lineHeight: 1.55, color: "rgba(232,238,244,.62)", marginTop: 10 }}>{s.why}</div>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: "auto", paddingTop: 16 }}>
                  <span style={{ font: `500 8.5px/1 ${MONO}`, letterSpacing: ".11em", textTransform: "uppercase", color: "rgba(232,238,244,.55)" }}>{s.e}</span>
                  <span aria-hidden="true" style={{ flex: 1, height: 1, background: "rgba(160,196,224,.14)" }} />
                  <span style={{ font: `500 8.5px/1 ${MONO}`, letterSpacing: ".11em", textTransform: "uppercase", color: "rgba(232,238,244,.55)" }}>{s.work.length} deliverables</span>
                </div>
              </div>
            )
          })}
        </div>
      </StepShell>

      {/* 04 · Prepared work */}
      <StepShell
        id="c04"
        n="04"
        title="Then Velova prepares the work."
        sub="Only for the route you selected."
      >
        <div
          data-a="panel"
          style={{
            position: "relative",
            boxSizing: "border-box",
            marginTop: 22,
            borderRadius: 20,
            border: "1px solid rgba(143,217,196,.26)",
            background: "linear-gradient(168deg, rgba(143,217,196,.05), #030509 74%)",
            overflow: "hidden",
          }}
        >
          <span aria-hidden="true" style={{ position: "absolute", top: 0, left: 0, right: 0, height: 1, overflow: "hidden" }}>
            <span style={{ position: "absolute", top: 0, width: "32%", height: 1, background: "linear-gradient(90deg, transparent, rgba(190,240,220,.9), transparent)", animation: "vxaShine 7s 1.6s cubic-bezier(.4,0,.5,1) infinite" }} />
          </span>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16, flexWrap: "wrap", padding: "18px 24px", borderBottom: "1px solid rgba(143,217,196,.16)" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 11 }}>
              <span aria-hidden="true" style={{ display: "flex", alignItems: "center", justifyContent: "center", width: 26, height: 26, borderRadius: 8, border: "1px solid rgba(143,217,196,.4)", background: "rgba(143,217,196,.08)" }}>
                <span style={{ display: "block", width: 12, height: 9, border: "1.3px solid #8FD9C4", borderTopWidth: 4, borderRadius: 2 }} />
              </span>
              <span style={{ font: `500 10px/1 ${MONO}`, letterSpacing: ".17em", textTransform: "uppercase", color: "rgba(190,233,245,.85)" }}>Handover package</span>
              <span style={{ font: `500 9px/1 ${MONO}`, letterSpacing: ".12em", textTransform: "uppercase", color: "#6BD5EC", border: "1px solid rgba(107,213,236,.45)", padding: "5px 8px", borderRadius: 6, whiteSpace: "nowrap" }}>{S.c}</span>
            </div>
            <span style={{ font: `400 10px/1 ${MONO}`, letterSpacing: ".1em", textTransform: "uppercase", color: "rgba(232,238,244,.55)" }}>
              {S.work.length} prepared items · {S.e.toLowerCase()}
            </span>
          </div>

          <div className="vx-handover-body">
            <div style={{ display: "flex", flexDirection: "column", padding: 14, borderRight: "1px solid rgba(143,217,196,.14)", background: "rgba(4,7,12,.35)", gap: 6 }}>
              {S.work.map(([t, d, kind], j) => {
                const on = j === safeItem
                return (
                  <span
                    key={j}
                    data-w="item"
                    role="button"
                    tabIndex={0}
                    aria-pressed={on}
                    onClick={() => {
                      setPaused(true)
                      setItem(j)
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault()
                        setPaused(true)
                        setItem(j)
                      }
                    }}
                    style={{
                      position: "relative",
                      overflow: "hidden",
                      cursor: "pointer",
                      boxSizing: "border-box",
                      display: "flex",
                      alignItems: "flex-start",
                      gap: 11,
                      padding: "12px 13px",
                      borderRadius: 11,
                      border: `1px solid ${on ? "rgba(143,217,196,.4)" : "transparent"}`,
                      background: on ? "rgba(143,217,196,.08)" : "transparent",
                    }}
                  >
                    <KindIcon kind={kind} on={on} />
                    <span style={{ minWidth: 0 }}>
                      <span style={{ display: "block", fontSize: 13.5, fontWeight: 500, color: on ? "#fff" : "rgba(240,245,249,.9)" }}>{t}</span>
                      <span style={{ display: "block", fontSize: 12, lineHeight: 1.45, color: "rgba(232,238,244,.58)", marginTop: 4 }}>{d}</span>
                    </span>
                    {on && !paused && !reduced ? (
                      <span aria-hidden="true" style={{ position: "absolute", left: 0, bottom: 0, height: 2, width: 0, background: "linear-gradient(90deg, #8FD9C4, rgba(143,217,196,.3))", animation: "vxaProg 3.2s linear forwards" }} />
                    ) : null}
                  </span>
                )
              })}
            </div>

            <div style={{ padding: "20px 22px", minHeight: 300 }}>
              <div key={`${strat}-${item}`} style={{ animation: reduced ? "none" : "vxaFade .3s ease both" }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 14 }}>
                  <span style={{ fontSize: 16, fontWeight: 500, color: "#F4F8FB" }}>{activeItem[0]}</span>
                  <span style={{ font: `400 9.5px/1 ${MONO}`, letterSpacing: ".1em", textTransform: "uppercase", color: "rgba(143,217,196,.75)", border: "1px solid rgba(143,217,196,.3)", padding: "5px 8px", borderRadius: 6, whiteSpace: "nowrap" }}>Ready to review</span>
                </div>
                <div style={{ fontSize: 13, lineHeight: 1.6, color: "rgba(232,238,244,.62)", marginTop: 8 }}>{activeItem[1]}</div>
                <div style={{ marginTop: 16 }}>
                  <PrevBody kind={activeItem[2]} />
                </div>
              </div>
            </div>
          </div>

          <div style={{ fontSize: 12.5, lineHeight: 1.6, color: "rgba(232,238,244,.55)", padding: "14px 24px", borderTop: "1px solid rgba(143,217,196,.14)" }}>
            Deliverables depend on the strategy and the gap. Not every Action produces every output.
          </div>
        </div>
      </StepShell>
    </>
  )
}
